/**
 * Sample data in the interface: the label, the filter, the banner, the
 * conversion dialog, and the settings wizard.
 *
 * A sample record is marked with text and an icon, never colour alone, and
 * the mark uses the information colour, never a finding colour. The filter
 * choice is remembered per organization in this browser.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { api } from "../lib/api.ts";
import { dateOnly } from "../lib/dates.ts";
import { useProblem, useRefresh } from "../lib/problem.ts";
import { useSession } from "../app-shell.tsx";
import { FilterFlyout } from "./filters.tsx";
import { Badge, Button, Code, Empty, Field, Loading, Note, Panel, Problem, classes, inputClass } from "./primitives.tsx";

// --- The label -------------------------------------------------------------

export interface SampleMarked {
  readonly originType?: string | null | undefined;
  readonly sampleState?: string | null | undefined;
}

/** SAMPLE, or Converted from sample. Nothing for working data. */
export function SampleLabel({ row, className }: { readonly row: SampleMarked; readonly className?: string }) {
  if (row.originType === "SAMPLE") {
    return (
      <span className={classes("inline-flex items-center gap-1 rounded-arc border border-info px-1.5 py-0.5 text-xs font-medium text-info", className)} title="Sample record.">
        <span aria-hidden="true">◈</span>SAMPLE
      </span>
    );
  }
  if (row.sampleState === "CONVERTED") {
    return (
      <span className={classes("inline-flex items-center gap-1 rounded-arc border border-divider px-1.5 py-0.5 text-xs text-muted", className)} title="Working data that started as a sample record.">
        <span aria-hidden="true">◇</span>From sample
      </span>
    );
  }
  return null;
}

// --- The filter -------------------------------------------------------------

export type SampleFilterValue = "all" | "working" | "sample";

const FILTER_LABEL: Record<SampleFilterValue, string> = { all: "All", working: "Working data", sample: "Sample data" };

/** The remembered filter of one organization. */
export function useSampleFilter(): [SampleFilterValue, (value: SampleFilterValue) => void] {
  const session = useSession();
  const tenantId = session.data?.selectedTenantId ?? "none";
  const key = `arcgrc.sample-filter.${tenantId}`;
  const [value, setValue] = useState<SampleFilterValue>("all");
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === "all" || stored === "working" || stored === "sample") setValue(stored);
      else setValue("all");
    } catch { setValue("all"); }
  }, [key]);
  const update = (next: SampleFilterValue) => {
    setValue(next);
    try { window.localStorage.setItem(key, next); } catch { /* A private window forgets it. */ }
  };
  return [value, update];
}

export function applySampleFilter<T extends SampleMarked>(rows: readonly T[], filter: SampleFilterValue): T[] {
  if (filter === "all") return [...rows];
  return rows.filter((row) => (filter === "sample") === (row.originType === "SAMPLE"));
}

/**
 * The All / Working data / Sample data control, shown only when sample rows
 * exist.
 *
 * It renders through the shared filter button, so a page whose only filter is
 * this one looks and behaves like a page with six.
 */
export function SampleFilter({ value, onChange, rows }: { readonly value: SampleFilterValue; readonly onChange: (value: SampleFilterValue) => void; readonly rows: readonly SampleMarked[] }) {
  const hasSample = rows.some((row) => row.originType === "SAMPLE" || row.sampleState === "CONVERTED");
  if (!hasSample && value === "all") return null;
  return (
    <FilterFlyout
      filters={[
        {
          key: "sample",
          label: "Show sample or working data",
          value,
          anyValue: "all",
          choices: (Object.keys(FILTER_LABEL) as SampleFilterValue[]).map((key) => ({
            value: key,
            label: FILTER_LABEL[key],
          })),
          onChange: (next: string) => onChange(next as SampleFilterValue),
        },
      ]}
    />
  );
}

// --- The banner --------------------------------------------------------------

interface Summary {
  readonly visible: boolean;
  readonly batches: number;
  readonly records: number;
  readonly canManage: boolean;
  readonly note: string;
  readonly answerLabels: Record<string, string>;
}

function useSampleSummary(enabled = true) {
  return useQuery({ queryKey: ["sample-summary"], queryFn: () => api.get<Summary>("/api/sample-data/summary"), enabled, retry: false });
}

/** The workspace banner while sample content is visible. */
export function SampleBanner() {
  const session = useSession();
  const summary = useSampleSummary(session.data?.selectedTenantId !== null && session.data?.selectedTenantId !== undefined);
  if (summary.data?.visible !== true) return null;
  return (
    <aside aria-label="Sample data notice" className="border-b border-info bg-paper px-4 py-1.5 text-sm tablet:px-6 tablet:py-2" data-testid="sample-banner">
      <span aria-hidden="true" className="mr-2 text-info">◈</span>
      {/*
        The short sentence is the one a phone shows: three lines of notice
        above the work is a notice nobody thanks you for. The sentence that
        explains what sample data does to the result stays on the wide
        screens, where it costs nothing.
      */}
      <span>This workspace contains sample data.</span>
      <span className="hidden tablet:inline">{" Sample records do not change the live compliance result."}</span>
      {summary.data.canManage && (
        <Link to="/settings" hash="sample-data" className="ml-3 text-accent underline">Manage sample data</Link>
      )}
    </aside>
  );
}

// --- Use as working data ----------------------------------------------------------

export type ConvertibleType =
  | "document" | "risk" | "monitoring_procedure" | "change_request" | "task" | "evidence_item"
  | "system" | "provider" | "question" | "finding" | "corrective_action" | "requirement_result" | "objective_result";

interface ConvertPreview {
  readonly name: string;
  readonly key: string | null;
  readonly alreadyConverted: string | null;
  readonly resets: string[];
  readonly dependencies: { type: string; id: string; name: string; key: string; converted: boolean }[];
  readonly targetAssessments: { id: string; name: string }[];
}

/** The button and dialog that copy a sample record into working data. */
export function UseAsWorkingData({ type, id, onDone, size = "small" }: { readonly type: ConvertibleType; readonly id: string; readonly onDone?: (() => void) | undefined; readonly size?: "small" | "normal" }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [includeDependencies, setIncludeDependencies] = useState(true);
  const [assessmentId, setAssessmentId] = useState("");
  const { problem, setProblem, onError } = useProblem();
  const [done, setDone] = useState<string | null>(null);
  const preview = useQuery({
    queryKey: ["convert-preview", type, id],
    queryFn: () => api.get<ConvertPreview>(`/api/records/${type}/${id}/convert-sample`),
    enabled: open,
  });
  const convert = useMutation({
    mutationFn: () => api.post<{ workingId: string; droppedLinks: string[] }>(`/api/records/${type}/${id}/convert-sample`, {
      includeDependencies, assessmentId: assessmentId || null,
    }),
    onSuccess: async (result) => {
      setDone(result.droppedLinks.length > 0 ? `Working record created. Links dropped: ${result.droppedLinks.join(", ")}.` : "Working record created.");
      await queryClient.invalidateQueries();
      onDone?.();
    },
    onError,
  });
  const needsAssessment = type === "requirement_result" || type === "objective_result";
  return (
    <>
      <Button size={size} variant="secondary" onClick={() => { setOpen(!open); setDone(null); setProblem(null); }}>Use as working data</Button>
      {open && (
        <div role="dialog" aria-label="Use as working data" className="mt-2 rounded-arc border border-info bg-paper p-3 text-sm" data-testid="convert-dialog">
          {preview.isLoading && <Loading what="the conversion preview" />}
          {preview.data && (
            <div className="flex flex-col gap-2">
              <p className="font-medium">{preview.data.name}</p>
              {preview.data.alreadyConverted !== null && <Note>This record was already used as working data.</Note>}
              <p className="text-muted">What changes:</p>
              <ul className="list-disc pl-5">{preview.data.resets.map((line) => <li key={line}>{line}</li>)}</ul>
              {preview.data.dependencies.length > 0 && (
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={includeDependencies} onChange={(event) => setIncludeDependencies(event.target.checked)} />
                  <span>
                    Also use the linked sample records as working data:{" "}
                    {preview.data.dependencies.map((dep) => `${dep.name}${dep.converted ? " (done)" : ""}`).join(", ")}.
                    Without this, the link is dropped.
                  </span>
                </label>
              )}
              {needsAssessment && (
                <Field id={`target-${id}`} label="Working assessment to copy into">
                  <select id={`target-${id}`} className={inputClass} value={assessmentId} onChange={(event) => setAssessmentId(event.target.value)}>
                    <option value="">Choose an assessment</option>
                    {preview.data.targetAssessments.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                  </select>
                </Field>
              )}
              {problem !== null && <Problem message={problem} />}
              {done !== null ? <p className="text-success">{done}</p> : (
                <div className="flex gap-2">
                  <Button size="small" variant="primary" disabled={convert.isPending || (needsAssessment && assessmentId === "")} onClick={() => convert.mutate()}>Confirm</Button>
                  <Button size="small" variant="quiet" onClick={() => setOpen(false)}>Cancel</Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// --- The settings wizard and management --------------------------------------------

interface Preview {
  readonly packId: string; readonly packVersion: string; readonly frameworkName: string;
  readonly catalogId: string; readonly catalogVersion: string; readonly profile: Record<string, string>;
  readonly questions: { key: string; label: string; options: string[]; answer: string; default: string }[];
  readonly counts: Record<string, number>;
  readonly conflicts: { table: string; key: string; name: string }[];
  readonly skipped: { key: string; reason: string }[];
  readonly controls: { explicit: number; fallback: number; missing: number; rejected: number; total: number };
  readonly objectives: { explicit: number; fallback: number; total: number };
  readonly documents: string[];
  readonly workspaceHasCustomerData: boolean;
  readonly existingBatches: number;
  readonly reused: number;
  readonly illustrative: Record<string, number>;
  readonly assurances: string[];
  readonly note: string;
}

interface Batch {
  readonly id: string; readonly frameworkKey: string; readonly packVersion: string; readonly profile: Record<string, string>;
  readonly state: string; readonly stage: string; readonly counts: Record<string, number>;
  readonly skipped: { key: string; reason: string }[]; readonly coverage: Record<string, unknown>;
  readonly failure: string | null; readonly createdByName: string | null; readonly createdAt: string;
  readonly completedAt: string | null; readonly removedAt: string | null; readonly restoreUntil: string | null; readonly converted: number;
}

interface RemovePreview {
  readonly batches: { id: string; frameworkKey: string }[];
  readonly counts: Record<string, number>;
  readonly converted: { table: string; id: string; name: string }[];
  readonly detach: { table: string; column: string; count: number }[];
  readonly blocked: string[];
  readonly confirmName: string;
}

const COUNT_LABEL: Record<string, string> = {
  teams: "Teams", providers: "Providers", systems: "Systems and locations", agreements: "Agreements", documents: "Policies and procedures",
  evidence_items: "Evidence references", evidence_links: "Evidence links", risks: "Risks", monitoring_procedures: "Monitoring procedures",
  monitoring_occurrences: "Monitoring occurrences", change_requests: "Changes", tasks: "Tasks and reviews", questions: "Questions",
  comments: "Comments", findings: "Findings", corrective_actions: "Corrective actions", approval_requests: "Approval requests",
  assessments: "Sample assessments", requirement_results: "Control responses", objective_results: "Objective responses",
  safeguard_decisions: "Safeguard responses", control_responses: "Control responses", objective_responses: "Objective responses",
  evidence: "Evidence references", monitoring: "Monitoring procedures", changes: "Changes", approvals: "Approval requests",
};

const STATE_LABEL: Record<string, string> = {
  SAMPLE_IMPLEMENTED: "Implemented", SAMPLE_PARTIAL: "Partial", SAMPLE_GAP: "Gap", SAMPLE_NA_CANDIDATE: "N/A candidate", SAMPLE_NOT_ASSESSED: "Not assessed",
};

function CountTable({ counts }: { readonly counts: Record<string, number> }) {
  const rows = Object.entries(counts).filter(([, n]) => n > 0);
  if (rows.length === 0) return <p className="text-sm text-muted">Nothing to add.</p>;
  return (
    <table className="density-compact text-sm">
      <tbody>{rows.map(([key, n]) => <tr key={key} className="border-b border-divider"><td className="pr-6 py-1">{COUNT_LABEL[key] ?? key.replace(/_/g, " ")}</td><td className="text-right">{n}</td></tr>)}</tbody>
    </table>
  );
}

/** One framework's wizard: explain, profile, preview, apply, result. */
function PopulateWizard({ frameworkKey, name, answerLabels, onDone }: { readonly frameworkKey: string; readonly name: string; readonly answerLabels: Record<string, string>; readonly onDone: () => void }) {
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [requestKey] = useState(() => `${frameworkKey}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const [batchId, setBatchId] = useState<string | null>(null);
  const { problem, onError } = useProblem();
  const preview = useQuery({
    queryKey: ["sample-preview", frameworkKey, answers],
    queryFn: () => api.post<Preview>(`/api/workspaces/${frameworkKey}/sample-data/preview`, { answers }),
    enabled: step >= 2,
  });
  const apply = useMutation({
    mutationFn: () => api.post<{ batchId: string }>(`/api/workspaces/${frameworkKey}/sample-data/apply`, { answers, requestKey }),
    onSuccess: (result) => { setBatchId(result.batchId); setStep(4); },
    onError,
  });
  const job = useQuery({
    queryKey: ["sample-job", batchId],
    queryFn: () => api.get<Batch>(`/api/sample-data/jobs/${batchId}`),
    enabled: batchId !== null && step === 4,
    refetchInterval: (query) => (query.state.data?.state === "running" || query.state.data?.state === "queued" ? 1000 : false),
  });
  useEffect(() => {
    if (step === 4 && job.data && (job.data.state === "completed" || job.data.state === "failed")) { setStep(5); onDone(); }
  }, [job.data, step, onDone]);
  const view = preview.data;

  return (
    <div className="flex flex-col gap-3 rounded-arc border border-info p-4" data-testid="populate-wizard">
      <p className="text-sm text-muted">{`Step ${step} of 5 · ${name}`}</p>
      {step === 1 && (
        <>
          <p className="font-medium">What sample data does</p>
          <ul className="list-disc pl-5 text-sm">
            <li>Adds realistic sample records to this workspace: policies, systems, risks, evidence references, monitoring, changes, tasks, and a response for every control.</li>
            <li>Marks every sample record. Sample records never change the real result, complete an approval, or send a message.</li>
            <li>Never overwrites a working record. Use any sample record as working data, or remove it at any time.</li>
          </ul>
          <div><Button variant="primary" onClick={() => setStep(2)}>Continue</Button></div>
        </>
      )}
      {step === 2 && (
        <>
          <p className="font-medium">Quick profile</p>
          <p className="text-sm text-muted">Each answer has a recommended default. Skip what you do not know.</p>
          {preview.isLoading && <Loading what="the questions" />}
          <div className="grid gap-3 md:grid-cols-2">
            {(view?.questions ?? []).map((question) => (
              <Field key={question.key} id={`q-${question.key}`} label={question.label} hint={`Default: ${answerLabels[question.default] ?? question.default}`}>
                <select id={`q-${question.key}`} className={inputClass} value={answers[question.key] ?? question.default} onChange={(event) => setAnswers({ ...answers, [question.key]: event.target.value })}>
                  {question.options.map((option) => <option key={option} value={option}>{answerLabels[option] ?? option}</option>)}
                </select>
              </Field>
            ))}
          </div>
          <div className="flex gap-2"><Button variant="primary" disabled={view === undefined} onClick={() => setStep(3)}>Preview</Button><Button variant="quiet" onClick={() => setStep(1)}>Back</Button></div>
        </>
      )}
      {step === 3 && view && (
        <div className="flex flex-col gap-3" data-testid="sample-preview">
          <p className="font-medium">Preview</p>
          <p className="text-sm text-muted">{`Pack ${view.packId} ${view.packVersion} · ${view.frameworkName} catalog ${view.catalogId} ${view.catalogVersion}`}</p>
          {view.workspaceHasCustomerData && <Problem message="This workspace holds working data. Sample records go beside it. Review the skipped records below." />}
          {view.existingBatches > 0 && <Note>{`An earlier sample batch exists. ${view.reused} shared records are reused, and only what is missing is added.`}</Note>}
          <div className="grid gap-4 md:grid-cols-2">
            <div><p className="text-sm font-medium">Records to add</p><CountTable counts={view.counts} /></div>
            <div className="text-sm">
              <p className="font-medium">Controls and objectives</p>
              <p>{`${view.controls.explicit} of ${view.controls.total} controls receive a sample response; ${view.controls.fallback} receive the safe fallback.`}</p>
              {view.objectives.total > 0 && <p>{`${view.objectives.explicit} of ${view.objectives.total} objectives receive a sample response.`}</p>}
              {(view.controls.missing > 0 || view.controls.rejected > 0) && <p className="text-attention">{`${view.controls.missing} target(s) missing, ${view.controls.rejected} rejected.`}</p>}
              <p className="mt-2 font-medium">Illustrative sample result</p>
              <p className="text-muted">Shown apart from the real result.</p>
              <p>{Object.entries(view.illustrative).map(([state, n]) => `${STATE_LABEL[state] ?? state} ${n}`).join(" · ")}</p>
            </div>
          </div>
          <details><summary className="cursor-pointer text-sm font-medium">{`${view.documents.length} sample documents`}</summary><ul className="list-disc pl-5 text-sm">{view.documents.map((title) => <li key={title}>{title}</li>)}</ul></details>
          {view.skipped.length > 0 && <details><summary className="cursor-pointer text-sm font-medium">{`${view.skipped.length} records skipped or reused`}</summary><ul className="list-disc pl-5 text-sm">{view.skipped.map((row) => <li key={row.key}><Code>{row.key}</Code> {row.reason}</li>)}</ul></details>}
          <ul className="list-disc pl-5 text-sm text-muted">{view.assurances.map((line) => <li key={line}>{line}</li>)}</ul>
          {problem !== null && <Problem message={problem} />}
          <div className="flex gap-2"><Button variant="primary" disabled={apply.isPending} onClick={() => apply.mutate()}>Add the sample records</Button><Button variant="quiet" onClick={() => setStep(2)}>Back</Button></div>
        </div>
      )}
      {step === 4 && (
        <div className="flex flex-col gap-2">
          <p className="font-medium">Adding the sample records</p>
          <ol className="text-sm">
            {["batch", "teams", "systems", "policies", "responses", "evidence", "risks", "monitoring", "changes", "coverage", "completed"].map((stage) => (
              <li key={stage} className={classes(job.data?.stage === stage ? "font-medium" : "text-muted")}>{job.data?.stage === stage ? "▸ " : "  "}{stage.replace(/_/g, " ")}</li>
            ))}
          </ol>
        </div>
      )}
      {step === 5 && job.data && (
        <div className="flex flex-col gap-3" data-testid="sample-result">
          {job.data.state === "failed" ? <Problem message={`The sample batch failed: ${job.data.failure ?? "unknown"}. Remove it from the batch list.`} /> : (
            <>
              <p className="font-medium">Sample records added</p>
              <div className="grid gap-4 md:grid-cols-2">
                <div><p className="text-sm font-medium">Added</p><CountTable counts={job.data.counts} /></div>
                <div className="text-sm">
                  {(() => { const c = job.data.coverage["controls"] as { explicit: number; fallback: number; total: number } | undefined; return c ? <p>{`Coverage: ${c.explicit + c.fallback} of ${c.total} controls, ${c.fallback} by fallback.`}</p> : null; })()}
                  <p className="mt-2">The sample includes open gaps, an overdue task, a failed recovery-set check, and decisions that need review.</p>
                  <p className="mt-2 flex flex-wrap gap-3">
                    <Link className="text-accent underline" to="/assessments">Assessments</Link>
                    <Link className="text-accent underline" to="/risks">Risk register</Link>
                    <Link className="text-accent underline" to="/documents">Policies</Link>
                    <Link className="text-accent underline" to="/monitoring">Monitoring</Link>
                    <Link className="text-accent underline" to="/changes">Changes</Link>
                  </p>
                </div>
              </div>
              {job.data.skipped.length > 0 && <details><summary className="cursor-pointer text-sm font-medium">{`${job.data.skipped.length} records skipped or reused`}</summary><ul className="list-disc pl-5 text-sm">{job.data.skipped.map((row) => <li key={row.key}><Code>{row.key}</Code> {row.reason}</li>)}</ul></details>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The Sample data panel of the settings page. */
export function SampleDataPanel({ workspaces }: { readonly workspaces: readonly { frameworkKey: string; name: string; state: string }[] }) {
  const queryClient = useQueryClient();
  const summary = useSampleSummary();
  const batches = useQuery({ queryKey: ["sample-batches"], queryFn: () => api.get<{ batches: Batch[] }>("/api/sample-data/batches") });
  const [populating, setPopulating] = useState<string | null>(null);
  const [removing, setRemoving] = useState<{ batchIds: string[] | null } | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const { problem, onError } = useProblem();
  const [removed, setRemoved] = useState<string | null>(null);
  const refresh = useRefresh([["sample-batches"], ["sample-summary"]]);
  const removePreview = useQuery({
    queryKey: ["sample-remove-preview", removing?.batchIds ?? null],
    queryFn: () => api.post<RemovePreview>(`/api/workspaces/${workspaces[0]?.frameworkKey ?? "cmmc-l1"}/sample-data/remove-preview`, { batchIds: removing?.batchIds ?? null }),
    enabled: removing !== null,
  });
  const remove = useMutation({
    mutationFn: () => api.post<{ counts: Record<string, number>; warnings: string[] }>(`/api/workspaces/${workspaces[0]?.frameworkKey ?? "cmmc-l1"}/sample-data/remove`, { batchIds: removing?.batchIds ?? null, confirmName }),
    onSuccess: async (result) => {
      setRemoved(`Removed. ${result.warnings.join(" ")}`.trim());
      setRemoving(null); setConfirmName("");
      queryClient.removeQueries({ queryKey: ["sample-remove-preview"] });
      await queryClient.invalidateQueries();
    },
    onError,
  });
  const restore = useMutation({
    mutationFn: (batchId: string) => api.post(`/api/workspaces/${workspaces[0]?.frameworkKey ?? "cmmc-l1"}/sample-data/restore/${batchId}`),
    onSuccess: async () => { setRemoved(null); await queryClient.invalidateQueries(); },
    onError,
  });

  if (summary.data?.canManage !== true) return null;
  const active = workspaces.filter((row) => row.state === "active");
  const completed = (batches.data?.batches ?? []).filter((row) => row.state === "completed");
  return (
    <Panel title="Sample data">
      <div id="sample-data" className="flex flex-col gap-3">
        <Note>{summary.data.note}</Note>
        {problem !== null && <Problem message={problem} />}
        {removed !== null && <p className="text-sm text-success">{removed}</p>}
        {active.length === 0 && <Empty message="Add a framework first." />}
        <div className="flex flex-wrap gap-2">
          {active.map((row) => (
            <Button key={row.frameworkKey} variant={populating === row.frameworkKey ? "secondary" : "main"} onClick={() => setPopulating(populating === row.frameworkKey ? null : row.frameworkKey)}>
              {`Populate with sample data: ${row.name}`}
            </Button>
          ))}
        </div>
        {populating !== null && (
          <PopulateWizard key={populating} frameworkKey={populating} name={active.find((row) => row.frameworkKey === populating)?.name ?? populating} answerLabels={summary.data.answerLabels} onDone={refresh} />
        )}

        <p className="mt-2 font-medium">Sample batches</p>
        {(batches.data?.batches.length ?? 0) === 0 && <p className="text-sm text-muted">No sample batches yet.</p>}
        <ul data-testid="sample-batches">
          {(batches.data?.batches ?? []).map((batch) => (
            <li key={batch.id} className="flex flex-wrap items-center gap-3 border-b border-divider py-2 text-sm">
              <span className="font-medium">{batch.frameworkKey.toUpperCase()}</span>
              <Badge>{batch.state}{batch.state === "running" ? ` · ${batch.stage}` : ""}</Badge>
              <span className="text-muted">{`pack ${batch.packVersion} · ${dateOnly(batch.createdAt)} · ${batch.createdByName ?? ""} · ${Object.values(batch.counts).reduce((a, b) => a + b, 0)} records · ${batch.converted} used as working data`}</span>
              {batch.failure !== null && <span className="text-error">{batch.failure}</span>}
              {batch.state === "completed" && <Button size="small" variant="secondary" onClick={() => { setRemoving({ batchIds: [batch.id] }); setRemoved(null); }}>Remove sample data</Button>}
              {batch.state === "removed" && <Button size="small" variant="secondary" disabled={restore.isPending} onClick={() => restore.mutate(batch.id)}>{`Restore (until ${dateOnly(batch.restoreUntil)})`}</Button>}
            </li>
          ))}
        </ul>
        {completed.length > 1 && <div><Button variant="secondary" onClick={() => { setRemoving({ batchIds: null }); setRemoved(null); }}>Remove all sample data</Button></div>}

        {removing !== null && (
          <div role="dialog" aria-label="Remove sample data" className="flex flex-col gap-2 rounded-arc border border-attention p-3 text-sm" data-testid="remove-dialog">
            {removePreview.isLoading && <Loading what="the removal preview" />}
            {removePreview.data && (
              <>
                <p className="font-medium">{`Remove ${removePreview.data.batches.length} sample batch(es)`}</p>
                <CountTable counts={removePreview.data.counts} />
                {removePreview.data.converted.length > 0 && <p>{`${removePreview.data.converted.length} record(s) used as working data stay.`}</p>}
                {removePreview.data.detach.map((row) => <p key={`${row.table}.${row.column}`} className="text-attention">{`${row.count} working ${row.table.replace(/_/g, " ")} record(s) lose the link in ${row.column.replace(/_/g, " ")}.`}</p>)}
                {removePreview.data.blocked.map((line) => <Problem key={line} message={line} />)}
                <p className="text-muted">Removed records can be restored for 30 days.</p>
                <Field id="confirm-name" label={`Type the organization name to confirm: ${removePreview.data.confirmName}`}>
                  <input id="confirm-name" className={inputClass} value={confirmName} onChange={(event) => setConfirmName(event.target.value)} />
                </Field>
                <div className="flex gap-2">
                  <Button variant="danger" disabled={remove.isPending || removePreview.data.blocked.length > 0 || confirmName.trim() !== removePreview.data.confirmName} onClick={() => remove.mutate()}>Remove sample data</Button>
                  <Button variant="quiet" onClick={() => setRemoving(null)}>Cancel</Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
